import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { apiService } from '../services/api'
import { useAuth } from './useAuth'

export interface StoreWithUsage {
  value: string
  assetCount: number
}

export interface UseStoresWithUsageReturn {
  stores: StoreWithUsage[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

// Cache local module-level chaveado por user.id (RBAC pode mudar entre users).
// TTL curto (60s) — alinhado com `useMaintenance`. Evita refetch a cada mount
// no mesmo user mas deixa os dados frescos o suficiente para o caso de uso.
//
// Evicção:
//   - Pruning lazy em cada `get` (remove entradas expiradas).
//   - `clearStoresCache()` deve ser invocado no logout para evitar leak
//     entre utilizadores (exportado para uso pelo handler de logout).
//   - `MAX_CACHE_ENTRIES` aplica um bound simples para impedir crescimento
//     ilimitado em tabs de longa duração com vários switches de user.
interface CacheEntry {
  stores: StoreWithUsage[]
  expiresAt: number
}

const storesCache = new Map<string, CacheEntry>()
const DEFAULT_TTL_MS = 60_000
const MAX_CACHE_ENTRIES = 16

const getCacheKey = (userId?: string | null) => `__stores_with_usage__::${userId ?? 'anonymous'}`

/**
 * Limpa entradas expiradas e aplica um bound simples (FIFO).
 * Chamado automaticamente em `get` e na escrita.
 */
function pruneExpiredEntries(now = Date.now()) {
  for (const [key, entry] of storesCache.entries()) {
    if (entry.expiresAt <= now) {
      storesCache.delete(key)
    }
  }
  // Bound simples: se ainda acima do máximo, remove o mais antigo (FIFO).
  while (storesCache.size > MAX_CACHE_ENTRIES) {
    const oldestKey = storesCache.keys().next().value
    if (oldestKey === undefined) break
    storesCache.delete(oldestKey)
  }
}

/**
 * Exportado para uso pelo handler de logout (`useAuth.logout`) ou outros
 * pontos de invalidação cross-component (ex.: troca de role).
 *
 * Sem subscribers globais: o hook prefere ser explícito. Quem quiser
 * garantir limpeza no logout importa esta função e invoca-a.
 */
export function clearStoresCache(): void {
  storesCache.clear()
}

/**
 * Hook que devolve a lista de lojas distintas com a contagem de ativos
 * (`{ value, assetCount }`), derivada do backend.
 *
 * Substitui o array hardcoded `LOCATION_OPTIONS` da PR #215.
 *
 * Comportamento:
 *  - Cache local module-level com TTL 60s (chaveado por user.id).
 *  - Pruning lazy em cada leitura (sem leak de entradas expiradas).
 *  - Bound via `MAX_CACHE_ENTRIES` (FIFO) para limitar uso de memória.
 *  - `refetch()` força nova busca (não respeita cache).
 *  - **Logout**: detecta `isAuthenticated === false` e limpa o cache +
 *    o estado local, sem leak para o próximo login.
 *  - **Race condition**: usa `requestIdRef` para garantir que apenas
 *    a última fetch iniciada afeta o state — fetches obsoletos são
 *    descartados sem mutar `stores`/`loading`/`error`.
 *  - **Unmount**: `isMountedRef` previne `setState`/cache write após
 *    o componente desmontar; in-flight ref é limpo no cleanup.
 */
export function useStoresWithUsage(): UseStoresWithUsageReturn {
  const { user, isAuthenticated } = useAuth()
  const [stores, setStores] = useState<StoreWithUsage[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Mantém a chave de cache estável por user.
  const cacheKey = useMemo(() => getCacheKey(user?.id), [user?.id])

  // Evita refetch concorrente se o componente montar várias vezes em paralelo
  // (mesma key).
  const inFlightRef = useRef<Promise<void> | null>(null)

  // Token monotonicamente crescente: garante que, em trocas rápidas de user
  // (login A → logout → login B), apenas a fetch mais recente afeta o state.
  // Fetches obsoletos descartam silenciosamente o resultado.
  const requestIdRef = useRef(0)

  // Previne setState/cache writes após unmount.
  const isMountedRef = useRef(true)

  const refetch = useCallback(async () => {
    if (!isAuthenticated) {
      setStores([])
      setError(null)
      clearStoresCache()
      return
    }

    if (inFlightRef.current) {
      return inFlightRef.current
    }

    // Captura o cacheKey no momento da chamada — se o user mudar durante
    // o await, vamos descartar o resultado (requestId mismatch).
    const myCacheKey = cacheKey
    const myRequestId = ++requestIdRef.current
    let activePromise: Promise<void> | null = null

    const work: Promise<void> = (async () => {
      setLoading(true)
      setError(null)
      try {
        const fresh = await apiService.getStoresWithUsage()
        // Race guard: se outra fetch começou entretanto (user trocou ou
        // refetch chamado de novo), descartar este resultado.
        if (!isMountedRef.current || requestIdRef.current !== myRequestId) {
          return
        }
        storesCache.set(myCacheKey, {
          stores: fresh,
          expiresAt: Date.now() + DEFAULT_TTL_MS,
        })
        pruneExpiredEntries()
        setStores(fresh)
      } catch (err) {
        if (!isMountedRef.current || requestIdRef.current !== myRequestId) {
          return
        }
        const message = err instanceof Error ? err.message : 'Erro ao carregar lojas'
        setError(message)
        console.error('[useStoresWithUsage] fetch failed', err)
      } finally {
        if (isMountedRef.current && requestIdRef.current === myRequestId) {
          setLoading(false)
        }
        if (activePromise && inFlightRef.current === activePromise) {
          inFlightRef.current = null
        }
      }
    })()

    activePromise = work
    inFlightRef.current = work
    return work
  }, [cacheKey, isAuthenticated])

  // Efeito principal: refetch on mount/auth change se cache expirado.
  useEffect(() => {
    if (!isAuthenticated) {
      // Logout / un-authenticated: limpa estado e cache. Sem leak de dados
      // entre utilizadores.
      setStores([])
      setError(null)
      clearStoresCache()
      return
    }

    pruneExpiredEntries()
    const cached = storesCache.get(cacheKey)
    if (cached && cached.expiresAt > Date.now()) {
      setStores(cached.stores)
      setError(null)
      return
    }

    void refetch()
  }, [cacheKey, isAuthenticated, refetch])

  // Cleanup no unmount: bloqueia setState futuros e descarta in-flight.
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      inFlightRef.current = null
    }
  }, [])

  return {
    stores,
    loading,
    error,
    refetch,
  }
}
